import {AmplitudeEvents} from '#/analytics/amplitude-event-types';
import {decimalsCount, decimalsMultiplier} from '#/utils/contract-consts';
import * as amplitude from '@amplitude/analytics-browser';
import {Land, LandMarketInfo} from '@shareland/shareland-common';

const amplitudeTrack = (event: AmplitudeEvents[keyof AmplitudeEvents]) => {
  amplitude.track(event);
};

const getLand = (land: Land) => ({
  name: land.name,
  city: land.city,
  state: land.state,
});

const tokenAmountToString = (numberToFormat: number | undefined, decimals: number = decimalsCount): string => {
  if (typeof numberToFormat === 'number') {
    return numberToFormat !== 0
      ? Intl.NumberFormat('en-US', {maximumFractionDigits: decimals, minimumFractionDigits: decimals}).format(
          numberToFormat / decimalsMultiplier,
        )
      : '0';
  }
  return '';
};

const getMarketPriceValue = (landMarketInfo: LandMarketInfo | undefined) =>
  landMarketInfo ? tokenAmountToString(landMarketInfo.marketPrice, 2) : '';
const getMarketVariationValue = (land: Land) => (land.syntheticMarketData.marketPrice.daily.ratio * 100).toFixed(1);

export const sendAmplitudeEvent = {
  fundingStarted: (params: Omit<AmplitudeEvents['fundingStarted'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'funding_started',
      ...params,
    });
  },
  fundingCompleted: (params: Omit<AmplitudeEvents['fundingCompleted'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'funding_completed',
    });
  },
  search: (params: Omit<AmplitudeEvents['search'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'search',
      ...params,
    });
  },
  exploreDataOrientation: (params: Omit<AmplitudeEvents['exploreDataOrientation'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'explore_data_orientation',
      ...params,
    });
  },
  viewProperty: (params: {land: Land; landMarketInfo: LandMarketInfo | undefined}) => {
    const {land, landMarketInfo} = params;
    const eventValue: AmplitudeEvents['viewProperty'] = {
      event_type: 'view_property',
      event_properties: {
        ...getLand(land),
        market_price: getMarketPriceValue(landMarketInfo),
        market_variation: getMarketVariationValue(land),
      },
    };

    amplitudeTrack(eventValue);
  },
  mapViewProperty: (params: Omit<AmplitudeEvents['mapViewProperty'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'map_view_property',
      ...params,
    });
  },
  transactionStarted: (params: Omit<AmplitudeEvents['transactionStarted'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'transaction_started',
      ...params,
    });
  },
  transactionCompleted: (params: Omit<AmplitudeEvents['transactionCompleted'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'transaction_completed',
      ...params,
    });
  },
  feedbackSubmitted: (params: Omit<AmplitudeEvents['feedbackSubmitted'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'feedback_submitted',
      ...params,
    });
  },
  login: (params: Omit<AmplitudeEvents['login'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'login',
      ...params,
    });
  },
  logout: (params: Omit<AmplitudeEvents['logout'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'logout',
      ...params,
    });
  },
  openAddFunds: (params: Omit<AmplitudeEvents['openAddFunds'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'open_add_funds',
      ...params,
    });
  },
  addFunds: (params: Omit<AmplitudeEvents['addFunds'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'add_funds',
      ...params,
    });
  },
  walletBalance: (params: Omit<AmplitudeEvents['walletBalance'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'wallet_balance',
      ...params,
    });
  },
  tradeButtonClick: () => {
    amplitudeTrack({
      event_type: 'trade_button_click',
    });
  },
  surveyModalShown: (params: Omit<AmplitudeEvents['surveyModalShown'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'survey_modal_shown',
      ...params,
    });
  },
  optedOutSurvey: (params: Omit<AmplitudeEvents['optedOutSurvey'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'opted_out_survey',
      ...params,
    });
  },
  submitSurvey: (params: Omit<AmplitudeEvents['submitSurvey'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'submit_survey',
      ...params,
    });
  },
  buyTradeInitiated: (params: Omit<AmplitudeEvents['buyTradeInitiated'], 'event_type'>) => {
    amplitudeTrack({
      event_type: 'buy_trade_initiated',
      ...params,
    });
  },
};
